import React from "react";
import { MDXProvider } from "@mdx-js/react";

// Custom UI components
import { ValueCard } from "../components/ui/ValueCard";
import { NotesPanel } from "../components/ui/NotesPanel";
import { PractitionerInsight } from "../components/ui/PractitionerInsight";
import { OfficialReference } from "../components/ui/OfficialReference";
import { CodeBlock, Pre } from "../components/ui/CodeBlock";
import { Note } from "../components/mdx/Callout";

// NEW: Interactive Python component
import { InteractivePythonBlock } from "../components/ui/InteractivePythonBlock";
import { SimplePythonBlock } from "../components/ui/SimplePythonBlock";
import { CodeSnippet } from "../components/ui/CodeSnippet";
import SimpleTest from "../components/ui/SimpleTest";

// Enhanced code block component that handles Python interactivity
const EnhancedCodeBlock = ({ children, className, ...props }: any) => {
  console.log('🔍 Code block detected:', { 
    children: typeof children, 
    className, 
    props,
    childrenPreview: typeof children === 'string' ? children.substring(0, 100) : 'not string'
  });
  const language = className?.replace('language-', '') || '';
  console.log('🐍 Language detected:', language);
  if (language === 'python') {
    const filename = props.filename || 'script.py';
    const title = props.title || 'Interactive Python Example';
    console.log('✅ Creating InteractivePythonBlock:', { filename, title });
    return (
      <InteractivePythonBlock 
        filename={filename} 
        title={title}
        editable={true}
      >
        {children}
      </InteractivePythonBlock>
    );
  }
  console.log('📝 Using regular CodeBlock for language:', language);
  return <CodeBlock className={className} {...props}>{children}</CodeBlock>;
};

// Test component to verify MDX component registration
const TestComponent = () => (
  <div className="bg-blue-100 p-4 rounded border-2 border-blue-500">
    <h4>✅ Test Component Working!</h4>
    <p>If you see this, MDX components are working correctly.</p>
  </div>
);

export const mdxComponents = {
  // UI Components
  ValueCard,
  NotesPanel,
  PractitionerInsight,
  OfficialReference,
  Note,

  // NEW: Interactive Python component
  InteractivePythonBlock,
  SimplePythonBlock,
  CodeSnippet,
  TestComponent,
  SimpleTest,

  // Enhanced code handling - ensure these override default MDX components
  code: EnhancedCodeBlock,
  pre: Pre,

  table: ({ children, ...props }: any) => (
    <div className="overflow-x-auto my-6">
      <table className="min-w-full border border-gray-200 dark:border-gray-700" {...props}>
        {children}
      </table>
    </div>
  ),

  thead: ({ children, ...props }: any) => (
    <thead className="bg-gray-50 dark:bg-gray-800" {...props}>
      {children}
    </thead>
  ),

  th: ({ children, ...props }: any) => (
    <th className="px-4 py-3 text-left font-bold text-gray-900 dark:text-white border-b-2 border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800" {...props}>
      {children}
    </th>
  ),

  td: ({ children, ...props }: any) => (
    <td className="px-4 py-3 text-gray-800 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700" {...props}>
      {children}
    </td>
  ),

  blockquote: ({ children, ...props }: any) => (
    <blockquote className="border-l-4 border-amber-500 pl-4 my-6 italic text-gray-800 dark:text-gray-100 bg-amber-50 dark:bg-amber-900/30 p-4 rounded-r" {...props}>
      {children}
    </blockquote>
  ),

  // Ensure paragraphs have good contrast
  p: ({ children, ...props }: any) => (
    <p className="text-gray-800 dark:text-gray-100 leading-relaxed" {...props}>
      {children}
    </p>
  ),

  // Lists with good contrast
  ul: ({ children, ...props }: any) => (
    <ul className="text-gray-800 dark:text-gray-100 list-disc pl-6 space-y-2" {...props}>
      {children}
    </ul>
  ),

  ol: ({ children, ...props }: any) => (
    <ol className="text-gray-800 dark:text-gray-100 list-decimal pl-6 space-y-2" {...props}>
      {children}
    </ol>
  ),

  li: ({ children, ...props }: any) => (
    <li className="text-gray-800 dark:text-gray-100" {...props}>
      {children}
    </li>
  ),

  // Strong/bold text
  strong: ({ children, ...props }: any) => (
    <strong className="font-bold text-gray-900 dark:text-white" {...props}>
      {children}
    </strong>
  ),

  // Headings with high contrast
  h1: ({ children, ...props }: any) => (
    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mt-8 mb-4 border-b-2 border-amber-500 pb-2" {...props}>
      {children}
    </h1>
  ),

  h2: ({ children, ...props }: any) => (
    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-8 mb-4" {...props}>
      {children}
    </h2>
  ),

  h3: ({ children, ...props }: any) => (
    <h3 className="text-xl font-bold text-gray-900 dark:text-white mt-6 mb-3" {...props}>
      {children}
    </h3>
  ),

  h4: ({ children, ...props }: any) => (
    <h4 className="text-lg font-bold text-gray-900 dark:text-white mt-4 mb-2" {...props}>
      {children}
    </h4>
  )
};

interface MDXWrapperProps {
  children: React.ReactNode;
}

export const MDXWrapper: React.FC<MDXWrapperProps> = ({ children }) => {
  return (
    <MDXProvider components={mdxComponents}>
      <div className="prose prose-lg dark:prose-invert max-w-none prose-p:text-gray-800 dark:prose-p:text-gray-100 prose-li:text-gray-800 dark:prose-li:text-gray-100">
        {children}
      </div>
    </MDXProvider>
  );
};

export default mdxComponents;
